import { getFulfillmentDetail } from '@/lib/preferences';
import { FulfillmentRegion } from '@/types/preferences';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getUserLocale } from 'get-user-locale';
import { DEFAULT_FULFILLMENT_REGION, MONOLITH_URL, USE_NEW_EXPRESS_CHECKOUT } from '@/constants';
import { useCheckoutData } from '@/hooks/use-checkout-data';
import { usePreviewStoreTheme } from '@/hooks/use-preview-store-theme';
import { useUserPreferences } from '@/hooks/use-user-preferences';
import { buildSkuWithQuantity } from '@/lib/product';
import useCartState from '@/stores/cart-state';
import { CartItem } from '@/types/cart';
import { useStoreSlug } from '@/hooks/use-store-slug';
import { Currency } from '@/gql';

const buildMonolithCheckoutUrl = (
  cart: CartItem[],
  storeSlug: string,
  storeUrl: string,
  currency: Currency,
  locale: string = 'en',
  fulfillmentRegion: FulfillmentRegion = DEFAULT_FULFILLMENT_REGION
) => {
  const skus = cart
    .map((item) => `${buildSkuWithQuantity(item.itemGroupId, item.variationId, item.sizeId, item.quantity)}`)
    .join(',');
  const url = new URL(MONOLITH_URL);
  url.pathname = '/buy/express_checkout';
  url.searchParams.set('currency', currency);
  url.searchParams.set('locale', locale);
  url.searchParams.set('payment_method', 'stripe');
  url.searchParams.set('ship_method', '1');
  url.searchParams.set('skus', skus);
  url.searchParams.set('store_id', storeSlug);
  url.searchParams.set('store_url', storeUrl);
  url.searchParams.set('fulfillment_region', fulfillmentRegion);
  return url.toString();
};

export const useGoToCheckout = () => {
  const { cart } = useCartState();
  const router = useRouter();
  const storeSlug = useStoreSlug();
  const locale = getUserLocale();
  const { createLocalCheckout } = useCheckoutData();
  const storeUrl = typeof window !== 'undefined' ? window.location.origin : `${storeSlug}.creator-spring.com`;
  const [creatingCheckout, setCreatingCheckout] = useState<boolean>(false);
  const previewStoreTheme = usePreviewStoreTheme();
  const previewMode = Boolean(previewStoreTheme?.preview);
  const { currency, fulfillment } = useUserPreferences();

  const canCheckout = useMemo(() => cart.length > 0 && !previewMode, [cart.length, previewMode]);

  const redirectToCheckout = useCallback(
    (legacy?: boolean) => {
      const requiresMonolithCheckout = () => currency !== 'USD' || fulfillment !== 'USA' || legacy;
      if (!canCheckout) {
        return;
      }
      const { region = DEFAULT_FULFILLMENT_REGION } = getFulfillmentDetail(fulfillment);
      if (requiresMonolithCheckout()) {
        router.push(buildMonolithCheckoutUrl(cart, storeSlug, storeUrl, currency, locale, region));
        return;
      } else {
        router.push('new_checkout');
      }
      if (USE_NEW_EXPRESS_CHECKOUT) {
        // New checkout flow doesn't require a checkout to be created
        // This makes it easier to know whether we need to update it, or create a new one, and stay consistent
        router.push(`/new_checkout`);
      } else {
        setCreatingCheckout(true);
        createLocalCheckout(storeSlug)
          .then((checkoutID) => {
            if (checkoutID) {
              router.push(`/checkout/${checkoutID}`);
            } else {
              throw new Error('Error creating checkout.');
            }
          })
          .catch(() => {
            // TODO: Is there a generic error page we can display here? We shouldn't get here - and it causes a blank page
            // the way it's configured now.
            router.push(`new_checkout/error}`);
          })
          .finally(() => {
            setCreatingCheckout(false);
          });
      }
    },
    [canCheckout, createLocalCheckout, storeSlug, fulfillment, currency, router, cart, storeUrl, locale]
  );

  return { redirectToCheckout, creatingCheckout, canCheckout };
};
